import React, { useState, useEffect } from "react";
import drEasyApi from "../../../Api/Customapi";
import { useClinics } from "../../../hooks/useClinics";
import { message, Select, Tooltip } from "antd";
import { QRCodeCanvas } from "qrcode.react";
import { IoReload } from "react-icons/io5";


/* Only keyframe animations that can't be expressed as Tailwind utilities */
const keyframes = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');
  @keyframes blink {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.35; }
  }
  @keyframes scanLine {
    0%   { top: 28px; opacity: 0; }
    8%   { opacity: 1; }
    92%  { opacity: 1; }
    100% { top: calc(100% - 28px); opacity: 0; }
  }
  .anim-blink      { animation: blink 2s ease-in-out infinite; }
  .anim-scan-line  { animation: scanLine 2.8s ease-in-out infinite; }
  .      { font-family: 'DM Sans', sans-serif; }
  .font-dm-mono    { font-family: 'DM Mono', monospace; }

  /* Ant Design Select overrides */
  .qr-select .ant-select-selector {
    border: 1.5px solid #e4e7ec !important;
    border-radius: 12px !important;
    background: #fafbfc !important;
    color: #1a2332 !important;
    font-family: 'DM Sans', sans-serif !important;
    font-size: 14px !important;
    box-shadow: none !important;
    transition: border-color 0.18s !important;
  }
  .qr-select .ant-select-selector:hover        { border-color: #298E9F !important; }
  .qr-select.ant-select-focused .ant-select-selector {
    border-color: #298E9F !important;
    box-shadow: 0 0 0 3px rgba(41,142,159,0.12) !important;
  }
  .qr-select .ant-select-arrow               { color: #9aa3af !important; }
  .qr-select .ant-select-selection-placeholder { color: #b8bec7 !important; }
  @media (max-width: 640px) {
  #qr-canvas canvas {
    width: 100% !important;
    height: auto !important;
  }
}
`;

export default function QrGeneration() {
  const { clinicsQuery } = useClinics();
  const [clinicId, setClinicId] = useState(
    clinicsQuery.data?.clinics?.[0]?.clinicId || null,
  );
  const [clinicName, setClinicName] = useState(
    clinicsQuery.data?.clinics?.[0]?.clinicName || "",
  );
  const [qrCodeString, setQrCodeString] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [qrVisible, setQrVisible] = useState(false);
  const [generatedAt, setGeneratedAt] = useState(null);

  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  async function generateQrCode() {
    if (!clinicId) {
      message.warning("Please select a clinic first");
      return;
    }
    try {
      setQrVisible(false);
      setLoading(true);
      const res = await drEasyApi.post("/api/Employees/GenerateQr", {
        clinicId,
      });
      setTimeout(() => {
        setQrCodeString(res.data);
        setGeneratedAt(new Date());
        setQrVisible(true);
      }, 200);
    } catch {
      message.error("Failed to generate QR code");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    if (!clinicId) return;
    generateQrCode();

    const interval = setInterval(() => {
      generateQrCode();
    }, 60000);

    return () => clearInterval(interval); // cleanup
  }, [clinicId]);

  function handleDownload() {
    const canvas = document.querySelector("#qr-canvas canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `qr-${clinicName || "clinic"}.png`;
    a.click();
  }

  const timeStr = currentTime.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });

  return (
    <>
      <style>{keyframes}</style>

      <div className="   h-screen bg-gray-100 flex items-start justify-center px-6 pt-3">
        {/* Card */}
        <div className="w-full max-w-7xl bg-white rounded-2xl overflow-hidden shadow-md">
          <div className="bg-primary px-8 pt-7 pb-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xl font-semibold text-white tracking-tight m-0 mb-1">
                  Select Clinic to Generate QR Code
                </p>
              </div>

              <div className="text-right">
                <div className="font-dm-mono text-lg font-medium text-white leading-none">
                  {timeStr}
                </div>
                <div className="flex items-center gap-2 justify-end">
                  <p className="text-[13px] font-semibold text-white/75 ">
                    {clinicName} ,
                  </p>
                  <div className="text-[11px] text-white/75 mt-1">
                    {currentTime.toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="px-8 pt-7 pb-8 flex flex-col gap-[18px]">
            <div className="flex gap-2 items-center">


              <div className="flex-1 text-[12px]  font-medium text-[#9aa3af] tracking-[0.04em] uppercase">
             
                <Select
                  className="qr-select"
                  placeholder="Select a clinic"
                  size="large"
                  showSearch
                  style={{ width: "100%" }}
                  value={clinicId}
                  loading={clinicsQuery.isLoading}
                  onChange={(value, option) => {
                    setClinicId(value);
                    setClinicName(option?.label || "");
                  }}
                  options={clinicsQuery.data?.clinics?.map((c) => ({
                    value: c.clinicId,
                    label: c.clinicName,
                  }))}
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </div>
              <Tooltip
                title={!clinicId ? "Please select a clinic first" : ""}
                placement="top"
              >
                <button
                  onClick={generateQrCode}
                  disabled={!clinicId || loading}
                  className={[
                    "flex-2 rounded-xl border-none    font-semibold",
                    "flex items-center justify-center gap-2 tracking-tight transition-all duration-150",
                    !clinicId || loading
                      ? "bg-[#e4e7ec] text-[#b8bec7]  shadow-none"
                      : "bg-primary text-white cursor-pointer hover:bg-[#1f7a8c] hover:shadow-[0_6px_20px_rgba(41,142,159,0.38)] hover:-translate-y-px active:translate-y-0 h-9 px-2",
                  ].join(" ")}
                >
                  {loading ? (
                    <>
                      <span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                      Generating…
                    </>
                  ) : (
                    <>
<IoReload />
                      Regenerate QR Code
                    </>
                  )}
                </button>
              </Tooltip>
            </div>

            {/* QR result / placeholder */}
            {qrCodeString ? (
              <div
                className={`transition-all duration-[350ms] ease-out ${
                  qrVisible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-3 pointer-events-none"
                }`}
              >
                <div className="border border-[#e4e7ec] rounded-[18px] overflow-hidden">
                  {/* Canvas area */}
                  <div className="relative flex justify-center items-center px-7 pt-7 pb-5 bg-white">
                    {/* Scan animation line */}
                    <span
                      className="anim-scan-line absolute left-7 right-7 h-[2px] rounded-sm"
                      style={{
                        background:
                          "linear-gradient(90deg, transparent 0%, rgba(41,142,159,0.45) 50%, transparent 100%)",
                      }}
                    />
                    <div id="qr-canvas" className="w-full flex justify-center">
                      <QRCodeCanvas
                        value={qrCodeString}
                        size={360}
                        level="H"
                        includeMargin={false}
                        style={{ maxWidth: "100%", height: "auto" }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* Placeholder */
              <div className="border border-dashed border-[#dde1e7] rounded-[18px] py-9 px-5 flex flex-col items-center gap-3">
                <div className="grid grid-cols-7 gap-[3px] opacity-45">
                  {Array.from({ length: 49 }).map((_, i) => (
                    <div
                      key={i}
                      className={`w-[14px] h-[14px] rounded-sm ${
                        i % 5 === 0
                          ? "bg-[#c4cad3]"
                          : i % 2 === 0
                            ? "bg-[#dde1e7]"
                            : "bg-[#eaecf0]"
                      }`}
                    />
                  ))}
                </div>
                <span className="font-dm-mono text-xs text-[#c4cad3] tracking-[0.06em]">
                  No QR generated yet
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
